/**
 * ChannelChip.jsx - Feb 2026 (iter1086h + iter1090)
 *
 * Tiny pill showing which channel a bank_feed_transactions row came
 * from. Renders inline in the transaction row.
 *
 * When the row has a `cross_channel_dup_of` pointer set (annotated by
 * `services/cross_channel_dup_detection.py`) we upgrade the chip into
 * a warning affordance so the user knows the same physical txn was
 * also imported through the OTHER channel and can be reviewed in the
 * cutover reconciliation view (Phase 2).
 *
 * iter1090 - Per Feb-2026 UX audit the row-level chip no longer names
 * the underlying provider (previously "MONO" / "PLAID"). Every synced
 * row simply shows "SYNC" / "Bank Sync". Provider identity is an
 * implementation detail we handle server-side.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { deriveChannel, CHANNEL_META, CHANNEL_OPEN_BANKING } from '../../utils/bankChannel';

const OB_PROVIDER_SHORT = {
  plaid: 'SYNC',
  mono: 'SYNC',
  open_banking_mono: 'SYNC',
  stitch: 'SYNC',
  teller: 'SYNC',
  tink: 'SYNC',
};

const OB_PROVIDER_LABEL = {
  plaid: 'Bank Sync',
  mono: 'Bank Sync',
  open_banking_mono: 'Bank Sync',
  stitch: 'Bank Sync',
  teller: 'Bank Sync',
  tink: 'Bank Sync',
};

export function ChannelChip({ item, size = 'sm', showLabel = false }) {
  if (!item) return null;
  // Prefer the persisted `channel` field (backfilled by iter1086h);
  // fall back to source-derived value for un-backfilled rows.
  const channel = item.channel || deriveChannel(item.source);
  const meta = CHANNEL_META[channel] || CHANNEL_META.statement_upload;
  const hasCrossDup = !!item.cross_channel_dup_of;

  // Provider-aware label for open-banking rows (US pilot).
  const srcKey = String(item.source || '').trim().toLowerCase();
  const providerShort = channel === CHANNEL_OPEN_BANKING ? (OB_PROVIDER_SHORT[srcKey] || 'SYNC') : null;
  const providerLabel = channel === CHANNEL_OPEN_BANKING ? (OB_PROVIDER_LABEL[srcKey] || 'Bank Sync') : null;
  const displayShort = providerShort || meta.short;
  const displayLabel = providerLabel || meta.label;

  const px = size === 'xs' ? 'px-1.5 py-0 text-[9px]' : 'px-2 py-0.5 text-[10px]';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-semibold tabular-nums ${meta.tone} ${px} ${hasCrossDup ? 'ring-1 ring-amber-400/60' : ''}`}
      title={hasCrossDup
        ? `${meta.title} · also present in the other channel - open cutover reconciliation`
        : meta.title}
      data-testid={`channel-chip-${channel}`}
    >
      {hasCrossDup && (
        <AlertTriangle className="w-2.5 h-2.5 text-amber-500" data-testid="cross-channel-dup-warning" />
      )}
      {showLabel ? displayLabel : displayShort}
    </span>
  );
}

export default ChannelChip;
